import { useEffect, useState } from 'react';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronLeft, faChevronRight, faReply, faPhone, faArrowUpRightFromSquare, faCheck, faCheckDouble } from "@fortawesome/free-solid-svg-icons";
import { replaceSymbolsToHtml, getWABodyVariables, convertDateToUtc } from "../../Common/Appcommon";
import dateFormat from "dateformat";
import '../Templates/CarouselModal.css';

const CarouselTemplatePreview = ({ components, formData, formInfo, source }) => {
    const [activeSlide, setActiveSlide] = useState(0);

    // Extract components
    const bodyComponent = components && components.find(c => c.type === 'BODY');
    const carouselComponent = components && components.find(c => c.type === 'CAROUSEL');
    const cards = carouselComponent ? carouselComponent.cards : [];

    // Reset active slide if out of bounds (e.g. current slide deleted)
    useEffect(() => {
        if (cards.length > 0 && activeSlide >= cards.length) {
            setActiveSlide(cards.length - 1);
        }
    }, [cards.length, activeSlide]);

    let displayTime = "07:14";
    let statusIcon = null;
    let statusColorClass = "";

    if (source === 'inbox' && formInfo) {
        if (formInfo['createdon']) {
             try {
                displayTime = dateFormat(convertDateToUtc(formInfo['createdon']), 'h:MM TT');
             } catch(e) {}
        }
        
        switch(formInfo['Status']) {
            case 'sent':
                statusIcon = faCheck;
                statusColorClass = "chat__msg-status-icon"; 
                break;
            case 'delivered':
                statusIcon = faCheckDouble;
                statusColorClass = "chat__msg-status-icon";
                break;
            case 'read':
                statusIcon = faCheckDouble;
                statusColorClass = "chat__msg-status-icon--blue";
                break;
            case 'failed':
            case 'undeliverable':
                break;
            default:
                break;
        }
    }

    const handleNextSlide = () => {
        if (activeSlide < cards.length - 1) {
            setActiveSlide(prev => prev + 1);
        }
    };

    const handlePrevSlide = () => {
        if (activeSlide > 0) {
            setActiveSlide(prev => prev - 1);
        }
    };

    const applyVariables = (text, type = 'BODY', cardIndex = null) => {
        if (!text) return text;
        let variables = getWABodyVariables(text);
        let result = text;
        if(variables && variables.length > 0){
            variables.forEach(v => {
                let prefix = (cardIndex !== null) ? `carousel_card_${cardIndex}_` : '';
                let varPrefix = type === 'HEADER' ? 'header_variable_' : 'body_variable_field_';
                let fallbackPrefix = type === 'HEADER' ? 'header_df_value_field_' : 'body_df_value_field_';
                let leadValuePrefix = type === 'HEADER' ? 'header_df_lead_value_field_' : 'body_df_lead_value_field_';

                let replacement = `{{${v}}}`; // Default to {{v}}
                let selectKey = `${prefix}${varPrefix}${v}_selected`;
                let fallbackKey = `${prefix}${fallbackPrefix}${v}`;
                let creationKey = `variable_${v}_fallback`; // Key used in CarouselModal creation
                let leadValueKey = `${prefix}${leadValuePrefix}${v}`; 

                let selected = formData && formData[selectKey];
                if(selected) {
                    if(selected.value === 'fallback') {
                        replacement = (formData && formData[fallbackKey]) || "-";
                    } else {
                        if (formData && formData[leadValueKey]) {
                            replacement = formData[leadValueKey];
                        } else if(selected.label){
                            replacement = "$" + selected.label + "$";
                        }else{
                            replacement = '-';
                        }
                    }
                } else if (formData && formData[creationKey]) {
                    // Fallback for Creation Modal
                    replacement = formData[creationKey];
                } else if (formData && formData[fallbackKey]) {
                    // Fallback for Inbox/History (Values only)
                    replacement = formData[fallbackKey];
                }
                result = result.replaceAll(`{{${v}}}`, replacement);
            });
        }
        return result;
    }

    const getFormattedText = (text, type = 'BODY', cardIndex = null) => {
        if (!text) return null;

        // 1. Substitute variables
        const textWithVars = applyVariables(text, type, cardIndex);
        const trimmed = textWithVars.trim();
        
        // Scenario 1: HTML Content
        if (/<[a-z][\s\S]*>/i.test(trimmed)) {
            return { __html: trimmed };
        }

        // Scenario 2: WhatsApp Markdown (returns React Elements)
        return replaceSymbolsToHtml(trimmed);
    };

    const bodyContent = getFormattedText(bodyComponent ? bodyComponent.text : '');
    return (
        <div className="carousel-template-preview-container">
            {/* Body Message Bubble */}
            <div className="wa-message-bubble">
                 <div className="wa-bubble-content">
                     {bodyContent && bodyContent.__html ? (
                        <div className="wa-message-text" dangerouslySetInnerHTML={bodyContent} />
                     ) : (
                        <div className="wa-message-text">{bodyContent}</div>
                     )}
                     <div className="wa-message-time" style={{display:'flex', alignItems:'center', justifyContent: 'flex-end', gap: '3px'}}>
                        <span>{displayTime}</span>
                        {statusIcon && (
                            <FontAwesomeIcon icon={statusIcon} className={statusColorClass} style={{fontSize: '0.7rem'}} />
                        )}
                     </div>
                 </div>
            </div>

            {/* Carousel Container */}
            {cards && cards.length > 0 && (
                <div className="wa-carousel-container">
                    <div 
                        className="wa-carousel-track" 
                        style={{ transform: `translateX(calc(-${activeSlide} * (100% + 10px)))` }}
                    >
                        {cards.map((card, index) => {
                            // Robust extraction
                            const headerComponent = card.components?.find(c => c.type === 'HEADER');
                            const bodyComponent = card.components?.find(c => c.type === 'BODY');
                            const buttonsComponent = card.components?.find(c => c.type === 'BUTTONS');
                            
                            const isVideo = (headerComponent?.format === 'VIDEO') || (card.mediaHeaderFormat === 'VIDEO');
                            
                            const uploadedMediaKey = `carousel_card_${index}_mediaUrl`;
                            const mediaUrl = (formData && formData[uploadedMediaKey]) || headerComponent?.url || card.mediaUrl;
                            
                            const bodyText = bodyComponent?.text || card.body;
                            let btnList = [];
                            if (buttonsComponent && buttonsComponent.buttons) {
                                btnList = buttonsComponent.buttons;
                            } else if (Array.isArray(card.suggestions)) {
                                btnList = card.suggestions;
                            } else if (Array.isArray(card.buttons)) {
                                btnList = card.buttons;
                            }
 
                            return (
                                <div className="wa-carousel-card" key={index}>
                                    <div className="wa-card-header-media position-relative">
                                        {mediaUrl ? (
                                            isVideo ? (
                                                <video src={mediaUrl} controls={false} />
                                            ) : (
                                                <img src={mediaUrl} alt="Header" />
                                            )
                                        ) : (
                                            <div className="wa-media-placeholder">
                                                {isVideo ? 'VIDEO' : 'IMAGE'}
                                            </div>
                                        )}
                                        
                                        {/* Arrows */}
                                        {index === activeSlide && activeSlide > 0 && (
                                             <div 
                                                 className="carousel-nav-arrow carousel-nav-prev wa-carousel-nav-prev" 
                                                 onClick={(e) => { e.stopPropagation(); handlePrevSlide(); }}
                                             >
                                                 <FontAwesomeIcon icon={faChevronLeft} />
                                             </div>
                                        )}
                                        {index === activeSlide && index < cards.length - 1 && (
                                             <div 
                                                 className="carousel-nav-arrow carousel-nav-next carousel-nav-arrow-right" 
                                                 onClick={(e) => { e.stopPropagation(); handleNextSlide(); }}
                                             >
                                                 <FontAwesomeIcon icon={faChevronRight} />
                                             </div>
                                        )}
                                    </div>
                                    
                                    <div className="wa-card-body">
                                        {headerComponent?.text && (
                                            (() => {
                                                const formattedHeader = getFormattedText(headerComponent.text, 'HEADER', index);
                                                return (
                                                    <div className="wa-card-title mb-1 font-weight-bold">
                                                        {formattedHeader && formattedHeader.__html ? (
                                                            <div dangerouslySetInnerHTML={formattedHeader} />
                                                        ) : (
                                                            <span>{formattedHeader}</span>
                                                        )}
                                                    </div>
                                                );
                                            })()
                                        )}
                                        {bodyText && (
                                            (() => {
                                                const formattedBody = getFormattedText(bodyText, 'BODY', index);
                                                return formattedBody && formattedBody.__html ? (
                                                    <div className="wa-card-body-text" dangerouslySetInnerHTML={formattedBody} />
                                                ) : (
                                                    <div className="wa-card-body-text">{formattedBody}</div>
                                                );
                                            })()
                                        )}
                                        { btnList && btnList.length > 0 && (
                                            <div className="wa-message-buttons">
                                                {btnList.map((btn, i) => {
                                                    const bType = (btn.type || "").toUpperCase();
                                                    return (
                                                        <button key={i} className="wa-btn">
                                                            {(bType === 'QUICK_REPLY' || bType === 'REPLY') && <FontAwesomeIcon icon={faReply} />}
                                                            {(bType === 'PHONE_NUMBER' || bType === 'CALL') && <FontAwesomeIcon icon={faPhone} />}
                                                            {(bType === 'URL') && <FontAwesomeIcon icon={faArrowUpRightFromSquare} />}
                                                            {btn.text || 'Button'}
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
};

export default CarouselTemplatePreview;